import { ActionStepType } from '@doss/prisma/types'
import { z } from 'zod'
import { defineSystemEvent } from './definition.js'
import { systemEventChangedFieldsSchema, systemEventFailureDataSchema } from './primitives.js'

// Display names and kinds ride along where the writer has them, so a reader needs no lookup of an
// entity that may since have been renamed or deleted. The action family prefixes them like its IDs.
const workflowData = z.strictObject({ workflow_id: z.uuid(), name: z.string().optional() })
const workflowTarget = workflowData.pick({ workflow_id: true })
const actionData = workflowTarget.extend({
  action_id: z.uuid(),
  action_name: z.string().optional(),
})
const actionTarget = actionData.pick({ workflow_id: true, action_id: true })
const stepData = actionTarget.extend({
  action_step_id: z.uuid(),
  action_step_name: z.string().optional(),
  action_step_type: z.enum(ActionStepType).optional(),
})
// A v2 workflow has no name; its slug is how people know it.
const workflowV2Data = workflowTarget.extend({
  version_id: z.uuid().optional(),
  slug: z.string().optional(),
})
const executionReference = {
  // References existing execution payload storage; no input/output payload copy.
  execution_id: z.string().min(1),
}
const actionRunData = actionData.extend({
  ...executionReference,
  trigger_type: z.string().min(1).optional(),
  action_name: z.string().min(1).optional(),
})
const stepRunData = stepData.extend(executionReference)
const workflowV2RunData = workflowV2Data.extend(executionReference)
const triggerData = workflowTarget.extend({
  // workflow_id names a v1 Workflow or a WorkflowV2, depending on this generation.
  workflow_generation: z.enum(['v1', 'v2']),
  trigger_id: z.uuid(),
  trigger_type: z.string().min(1),
  action_id: z.uuid().optional(),
})
const dispatchData = triggerData.extend({ dispatched_execution_id: z.string().min(1).optional() })
// ObjectStoreTableSync links a table to its object-store integration.
const objectSyncData = z.strictObject({
  object_sync_id: z.uuid(),
  table_id: z.uuid(),
  integration_id: z.uuid(),
})

export const workflowSystemEvents = {
  workflowCreateSuccess: defineSystemEvent({
    type: 'CREATE_WORKFLOW',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW'],
    data: workflowData,
  }),
  workflowCreateFailed: defineSystemEvent({
    type: 'CREATE_WORKFLOW',
    status: 'FAIL',
    entityTypes: ['WORKFLOW'],
    data: systemEventFailureDataSchema.extend({ name: z.string().optional() }),
  }),
  workflowUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_WORKFLOW',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW'],
    data: workflowData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  workflowUpdateFailed: defineSystemEvent({
    type: 'UPDATE_WORKFLOW',
    status: 'FAIL',
    entityTypes: ['WORKFLOW'],
    data: workflowData.extend(systemEventFailureDataSchema.shape),
  }),
  workflowDeleteSuccess: defineSystemEvent({
    type: 'DELETE_WORKFLOW',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW'],
    data: workflowData,
  }),
  workflowDeleteFailed: defineSystemEvent({
    type: 'DELETE_WORKFLOW',
    status: 'FAIL',
    entityTypes: ['WORKFLOW'],
    data: workflowData.extend(systemEventFailureDataSchema.shape),
  }),
  actionCreateSuccess: defineSystemEvent({
    type: 'CREATE_ACTION',
    status: 'SUCCESS',
    entityTypes: ['ACTION'],
    data: actionData,
  }),
  actionCreateFailed: defineSystemEvent({
    type: 'CREATE_ACTION',
    status: 'FAIL',
    entityTypes: ['ACTION', 'WORKFLOW'],
    data: actionData.omit({ action_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  actionUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_ACTION',
    status: 'SUCCESS',
    entityTypes: ['ACTION'],
    data: actionData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  actionUpdateFailed: defineSystemEvent({
    type: 'UPDATE_ACTION',
    status: 'FAIL',
    entityTypes: ['ACTION'],
    data: actionData.extend(systemEventFailureDataSchema.shape),
  }),
  actionDeleteSuccess: defineSystemEvent({
    type: 'DELETE_ACTION',
    status: 'SUCCESS',
    entityTypes: ['ACTION'],
    data: actionData,
  }),
  actionDeleteFailed: defineSystemEvent({
    type: 'DELETE_ACTION',
    status: 'FAIL',
    entityTypes: ['ACTION'],
    data: actionData.extend(systemEventFailureDataSchema.shape),
  }),
  // A start is its own successful fact; the later RUN_* event records the execution's outcome.
  actionRunStarted: defineSystemEvent({
    type: 'RUN_ACTION_STARTED',
    status: 'SUCCESS',
    entityTypes: ['ACTION'],
    data: actionRunData,
  }),
  actionRunSuccess: defineSystemEvent({
    type: 'RUN_ACTION',
    status: 'SUCCESS',
    entityTypes: ['ACTION'],
    data: actionRunData,
  }),
  actionRunFailed: defineSystemEvent({
    type: 'RUN_ACTION',
    status: 'FAIL',
    entityTypes: ['ACTION'],
    data: actionRunData.extend(systemEventFailureDataSchema.shape),
  }),
  actionStepCreateSuccess: defineSystemEvent({
    type: 'CREATE_ACTION_STEP',
    status: 'SUCCESS',
    entityTypes: ['ACTION_STEP'],
    data: stepData,
  }),
  actionStepCreateFailed: defineSystemEvent({
    type: 'CREATE_ACTION_STEP',
    status: 'FAIL',
    entityTypes: ['ACTION_STEP', 'ACTION'],
    // The step that was asked for, when the request named it.
    data: stepData.omit({ action_step_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  actionStepUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_ACTION_STEP',
    status: 'SUCCESS',
    entityTypes: ['ACTION_STEP'],
    data: stepData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  actionStepUpdateFailed: defineSystemEvent({
    type: 'UPDATE_ACTION_STEP',
    status: 'FAIL',
    entityTypes: ['ACTION_STEP'],
    data: stepData.extend(systemEventFailureDataSchema.shape),
  }),
  actionStepDeleteSuccess: defineSystemEvent({
    type: 'DELETE_ACTION_STEP',
    status: 'SUCCESS',
    entityTypes: ['ACTION_STEP'],
    data: stepData,
  }),
  actionStepDeleteFailed: defineSystemEvent({
    type: 'DELETE_ACTION_STEP',
    status: 'FAIL',
    entityTypes: ['ACTION_STEP'],
    data: stepData.extend(systemEventFailureDataSchema.shape),
  }),
  actionStepRunStarted: defineSystemEvent({
    type: 'RUN_ACTION_STEP_STARTED',
    status: 'SUCCESS',
    entityTypes: ['ACTION_STEP'],
    data: stepRunData,
  }),
  actionStepRunSuccess: defineSystemEvent({
    type: 'RUN_ACTION_STEP',
    status: 'SUCCESS',
    entityTypes: ['ACTION_STEP'],
    data: stepRunData,
  }),
  actionStepRunFailed: defineSystemEvent({
    type: 'RUN_ACTION_STEP',
    status: 'FAIL',
    entityTypes: ['ACTION_STEP'],
    data: stepRunData.extend(systemEventFailureDataSchema.shape),
  }),
  workflowV2CreateSuccess: defineSystemEvent({
    type: 'CREATE_WORKFLOW_V2',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2Data,
  }),
  workflowV2CreateFailed: defineSystemEvent({
    type: 'CREATE_WORKFLOW_V2',
    status: 'FAIL',
    entityTypes: ['WORKFLOW_V2'],
    data: systemEventFailureDataSchema.extend({ slug: z.string().optional() }),
  }),
  workflowV2UpdateSuccess: defineSystemEvent({
    type: 'UPDATE_WORKFLOW_V2',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2Data.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  workflowV2UpdateFailed: defineSystemEvent({
    type: 'UPDATE_WORKFLOW_V2',
    status: 'FAIL',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2Data.extend(systemEventFailureDataSchema.shape),
  }),
  workflowV2DeleteSuccess: defineSystemEvent({
    type: 'DELETE_WORKFLOW_V2',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2Data,
  }),
  workflowV2DeleteFailed: defineSystemEvent({
    type: 'DELETE_WORKFLOW_V2',
    status: 'FAIL',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2Data.extend(systemEventFailureDataSchema.shape),
  }),
  workflowV2RunStarted: defineSystemEvent({
    type: 'RUN_WORKFLOW_V2_STARTED',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2RunData,
  }),
  workflowV2RunSuccess: defineSystemEvent({
    type: 'RUN_WORKFLOW_V2',
    status: 'SUCCESS',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2RunData,
  }),
  workflowV2RunFailed: defineSystemEvent({
    type: 'RUN_WORKFLOW_V2',
    status: 'FAIL',
    entityTypes: ['WORKFLOW_V2'],
    data: workflowV2RunData.extend(systemEventFailureDataSchema.shape),
  }),
  triggerCreateSuccess: defineSystemEvent({
    type: 'CREATE_TRIGGER',
    status: 'SUCCESS',
    entityTypes: ['TRIGGER'],
    data: triggerData,
  }),
  triggerCreateFailed: defineSystemEvent({
    type: 'CREATE_TRIGGER',
    status: 'FAIL',
    entityTypes: ['TRIGGER', 'WORKFLOW', 'WORKFLOW_V2', 'ACTION'],
    data: triggerData.omit({ trigger_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  triggerUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_TRIGGER',
    status: 'SUCCESS',
    entityTypes: ['TRIGGER'],
    data: triggerData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  triggerUpdateFailed: defineSystemEvent({
    type: 'UPDATE_TRIGGER',
    status: 'FAIL',
    entityTypes: ['TRIGGER'],
    data: triggerData.extend(systemEventFailureDataSchema.shape),
  }),
  triggerDeleteSuccess: defineSystemEvent({
    type: 'DELETE_TRIGGER',
    status: 'SUCCESS',
    entityTypes: ['TRIGGER'],
    data: triggerData,
  }),
  triggerDeleteFailed: defineSystemEvent({
    type: 'DELETE_TRIGGER',
    status: 'FAIL',
    entityTypes: ['TRIGGER'],
    data: triggerData.extend(systemEventFailureDataSchema.shape),
  }),
  triggerFireSuccess: defineSystemEvent({
    type: 'FIRE_TRIGGER',
    status: 'SUCCESS',
    entityTypes: ['TRIGGER'],
    data: dispatchData,
  }),
  triggerFireFailed: defineSystemEvent({
    type: 'FIRE_TRIGGER',
    status: 'FAIL',
    entityTypes: ['TRIGGER'],
    data: dispatchData.extend(systemEventFailureDataSchema.shape),
  }),
  objectSyncCreateSuccess: defineSystemEvent({
    type: 'CREATE_OBJECT_SYNC',
    status: 'SUCCESS',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData,
  }),
  objectSyncCreateFailed: defineSystemEvent({
    type: 'CREATE_OBJECT_SYNC',
    status: 'FAIL',
    entityTypes: ['OBJECT_SYNC', 'TABLE'],
    data: objectSyncData.omit({ object_sync_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  objectSyncUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_OBJECT_SYNC',
    status: 'SUCCESS',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData.extend({ changed_fields: systemEventChangedFieldsSchema }),
  }),
  objectSyncUpdateFailed: defineSystemEvent({
    type: 'UPDATE_OBJECT_SYNC',
    status: 'FAIL',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData.extend(systemEventFailureDataSchema.shape),
  }),
  objectSyncDeleteSuccess: defineSystemEvent({
    type: 'DELETE_OBJECT_SYNC',
    status: 'SUCCESS',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData,
  }),
  objectSyncDeleteFailed: defineSystemEvent({
    type: 'DELETE_OBJECT_SYNC',
    status: 'FAIL',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData.extend(systemEventFailureDataSchema.shape),
  }),
  objectSyncTriggerSuccess: defineSystemEvent({
    type: 'TRIGGER_OBJECT_SYNC',
    status: 'SUCCESS',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData,
  }),
  objectSyncTriggerFailed: defineSystemEvent({
    type: 'TRIGGER_OBJECT_SYNC',
    status: 'FAIL',
    entityTypes: ['OBJECT_SYNC'],
    data: objectSyncData.extend(systemEventFailureDataSchema.shape),
  }),
}
